import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useI18n } from "../../context/I18nContext";
import { pathFor } from "../../lib/routes";

export default function NewsletterForm() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({
    name: "",
    email: "",
    sector: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});

  const sectors = t("newsletter.sectors");

  const handleChange = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [field]: val }));
  };

  const validate = () => {
    const errs = {};
    if (!values.name.trim()) errs.name = t("newsletter.errorRequired");
    if (!values.email.trim()) errs.email = t("newsletter.errorRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      errs.email = t("newsletter.errorEmail");
    if (!values.consent) errs.consent = t("newsletter.errorConsent");
    return errs;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      const existing = JSON.parse(localStorage.getItem("sb_leads") || "[]");
      existing.push({
        ...values,
        lang,
        createdAt: new Date().toISOString(),
      });
      localStorage.setItem("sb_leads", JSON.stringify(existing));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "newsletter_lead_submit",
        sector: values.sector || "unspecified",
      });
    } catch {}
    navigate("/thank_you");
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="newsletter-form"
      noValidate
      className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm"
    >
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label
            htmlFor="lead-name"
            className="block text-sm font-medium text-slate-800"
          >
            {t("newsletter.nameLabel")}
          </label>
          <input
            id="lead-name"
            type="text"
            value={values.name}
            onChange={handleChange("name")}
            data-testid="newsletter-name-input"
            placeholder={t("newsletter.namePlaceholder")}
            className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
          />
          {errors.name && (
            <p
              className="mt-1 text-xs text-red-600"
              data-testid="newsletter-name-error"
            >
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="lead-email"
            className="block text-sm font-medium text-slate-800"
          >
            {t("newsletter.emailLabel")}
          </label>
          <input
            id="lead-email"
            type="email"
            value={values.email}
            onChange={handleChange("email")}
            data-testid="newsletter-email-input"
            placeholder={t("newsletter.emailPlaceholder")}
            className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
          />
          {errors.email && (
            <p
              className="mt-1 text-xs text-red-600"
              data-testid="newsletter-email-error"
            >
              {errors.email}
            </p>
          )}
        </div>
        <div className="md:col-span-2">
          <label
            htmlFor="lead-sector"
            className="block text-sm font-medium text-slate-800"
          >
            {t("newsletter.sectorLabel")}
          </label>
          <select
            id="lead-sector"
            value={values.sector}
            onChange={handleChange("sector")}
            data-testid="newsletter-sector-select"
            className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-slate-900 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
          >
            <option value="">{t("newsletter.sectorPlaceholder")}</option>
            {sectors.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={values.consent}
              onChange={handleChange("consent")}
              data-testid="newsletter-consent-checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span>
              {t("newsletter.consent")}{" "}
              <Link
                to={pathFor("privacy", lang)}
                className="text-emerald-700 underline"
                data-testid="newsletter-privacy-link"
              >
                {t("nav.privacy")}
              </Link>
            </span>
          </label>
          {errors.consent && (
            <p
              className="mt-1 text-xs text-red-600"
              data-testid="newsletter-consent-error"
            >
              {errors.consent}
            </p>
          )}
        </div>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-md">
          {t("newsletter.disclaimer")}
        </p>
        <button
          type="submit"
          data-testid="newsletter-submit-button"
          className="inline-flex items-center justify-center rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
        >
          {t("newsletter.submit")}
        </button>
      </div>
    </form>
  );
}
